import { useEffect, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Sparkles, Check, RefreshCcw, TrendingUp, Trash2, XCircle, Wallet, Pencil, Repeat } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import BestCardHint from "@/components/BestCardHint";
import PrintPageButton from "@/components/PrintPageButton";
import SubscriptionCancelDialog from "@/components/SubscriptionCancelDialog";
import EmptyState from "@/components/EmptyState";
import SubscriptionIntelligencePanel from "@/components/SubscriptionIntelligencePanel";

const EMPTY = { name: "", vendor: "", card_id: "", amount: 0, frequency: "Monthly", next_billing_date: "", business_personal: "Business", auto_renewal: true, active: true, remarks: "" };

export default function Subscriptions() {
    const { hasRole } = useAuth();
    const [sp] = useSearchParams();
    const tab = sp.get("tab") || "";
    const [cards, setCards] = useState([]);
    const [rows, setRows] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [candidatesLoaded, setCandidatesLoaded] = useState(false);
    const [open, setOpen] = useState(false);
    const [f, setF] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);
    const [scanBusy, setScanBusy] = useState(false);
    const [scanMonths, setScanMonths] = useState(6);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [cancellations, setCancellations] = useState({ count: 0, total_annual_saving: 0, events: [] });

    // Sidebar deep-link filters the visible list. `review` narrows to
    // pending review candidates; `cancelled` narrows to the cancellation
    // history block. Default shows everything active.
    const filteredRows = tab === "cancelled"
        ? rows.filter((r) => !r.active)
        : rows;

    const load = () => api.get("/subscriptions").then((r) => setRows(r.data));
    const del = async (id, name) => {
        if (!window.confirm(`Delete subscription "${name}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/subscriptions/${id}`);
            toast.success("Subscription deleted");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const loadCandidates = async () => {
        if (!hasRole("admin", "finance")) return;
        try {
            const { data } = await api.get("/subscriptions/detect/candidates");
            setCandidates(data || []);
        } catch {
            setCandidates([]);
        } finally {
            setCandidatesLoaded(true);
        }
    };
    const scan = async () => {
        setScanBusy(true);
        try {
            const { data } = await api.post("/subscriptions/detect/scan", { months: Number(scanMonths) });
            toast.success(`Scanned ${data.expenses_analysed} expenses · ${data.curated_new + data.pattern_new} new candidates.`);
            await loadCandidates();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setScanBusy(false); }
    };
    useEffect(() => {
        api.get("/cards").then((r) => setCards(r.data));
        load();
        loadCandidates();
        api.get("/subscriptions/cancellations/history").then((r) => setCancellations(r.data)).catch(() => {});
    }, []);

    const acceptCandidate = async (cand) => {
        try {
            await api.post(`/subscriptions/detect/candidates/${cand.id}/accept`, {
                business_personal: "Personal",
            });
            toast.success(`Added ${cand.name}`);
            setCandidates((cs) => cs.filter((c) => c.id !== cand.id));
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const dismissCandidate = async (cand) => {
        try {
            await api.post(`/subscriptions/detect/candidates/${cand.id}/reject`);
            setCandidates((cs) => cs.filter((c) => c.id !== cand.id));
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
    const setInput = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...f, amount: Number(f.amount || 0) };
            if (editingId) {
                await api.patch(`/subscriptions/${editingId}`, payload);
                toast.success("Subscription updated");
            } else {
                await api.post("/subscriptions", payload);
                toast.success("Subscription added");
            }
            setOpen(false);
            setEditingId(null);
            setF(EMPTY);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const openAdd = () => {
        setEditingId(null);
        setF(EMPTY);
        setOpen(true);
    };
    const openEdit = (row) => {
        setEditingId(row.id);
        setF({
            name: row.name || "",
            vendor: row.vendor || "",
            card_id: row.card_id || "",
            amount: row.amount || 0,
            frequency: row.frequency || "Monthly",
            next_billing_date: (row.next_billing_date || "").slice(0, 10),
            business_personal: row.business_personal || "Business",
            auto_renewal: row.auto_renewal !== false,
            active: row.active !== false,
            remarks: row.remarks || "",
        });
        setOpen(true);
    };

    const cardMap = Object.fromEntries(cards.map((c) => [c.id, c]));

    return (
        <div data-testid="subscriptions-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Subscriptions</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Recurring charges.</h1>
                    <p className="text-sm text-stone-500 mt-3">{rows.length} active · {fmtMoney(rows.filter(r => r.active && r.frequency === "Monthly").reduce((s, r) => s + Number(r.amount || 0), 0))}/mo</p>
                </div>
                {hasRole("admin", "finance") && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <PrintPageButton />
                        <Input type="number" value={scanMonths} onChange={(e) => setScanMonths(e.target.value)}
                               min={1} max={24} className="w-20 font-mono-figure"
                               data-testid="subs-scan-months" />
                        <Button onClick={scan} disabled={scanBusy} variant="outline"
                                data-testid="subs-scan-btn">
                            <RefreshCcw size={13} className={`mr-1.5 ${scanBusy ? "animate-spin" : ""}`} />
                            {scanBusy ? "Scanning…" : "Detect"}
                        </Button>
                        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setF(EMPTY); } }}>
                            <DialogTrigger asChild>
                                <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="add-sub-btn" onClick={openAdd}><Plus size={16} className="mr-1.5" /> Add subscription</Button>
                            </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? "Edit subscription" : "New subscription"}</DialogTitle></DialogHeader>
                            <form onSubmit={submit} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="col-span-2"><Label className="eyebrow">Name</Label><Input required value={f.name} onChange={setInput("name")} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">Vendor</Label><Input value={f.vendor} onChange={setInput("vendor")} className="mt-1.5" /></div>
                                    <div>
                                        <Label className="eyebrow">Card</Label>
                                        <Select value={f.card_id} onValueChange={set("card_id")}>
                                            <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select card" /></SelectTrigger>
                                            <SelectContent>
                                                {cards.map((c) => <SelectItem key={c.id} value={c.id}>{c.bank_name} · •••• {c.last_4}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div><Label className="eyebrow">Amount</Label><Input type="number" value={f.amount} onChange={setInput("amount")} className="mt-1.5" /></div>
                                    <div>
                                        <Label className="eyebrow">Frequency</Label>
                                        <Select value={f.frequency} onValueChange={set("frequency")}>
                                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Monthly">Monthly</SelectItem>
                                                <SelectItem value="Quarterly">Quarterly</SelectItem>
                                                <SelectItem value="Yearly">Yearly</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div><Label className="eyebrow">Next billing date</Label><Input type="date" value={f.next_billing_date} onChange={setInput("next_billing_date")} className="mt-1.5" /></div>
                                    <div>
                                        <Label className="eyebrow">Business / Personal</Label>
                                        <Select value={f.business_personal} onValueChange={set("business_personal")}>
                                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Business">Business</SelectItem>
                                                <SelectItem value="Personal">Personal</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex items-center justify-between border border-stone-200 rounded-md px-3 py-2.5">
                                        <div className="text-sm">Auto renewal</div>
                                        <Switch checked={f.auto_renewal} onCheckedChange={set("auto_renewal")} />
                                    </div>
                                </div>
                                <div><Label className="eyebrow">Remarks</Label><Textarea rows={2} value={f.remarks} onChange={setInput("remarks")} className="mt-1.5" /></div>
                                <Button type="submit" className="w-full bg-stone-900 hover:bg-stone-800 text-white">{editingId ? "Save changes" : "Add subscription"}</Button>
                            </form>
                        </DialogContent>
                        </Dialog>
                    </div>
                )}
            </div>

            {/* Cumulative savings from cancellations */}
            {cancellations.count > 0 && (
                <div className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50/50 px-4 py-3 flex items-center gap-3" data-testid="subs-savings-banner">
                    <div className="w-9 h-9 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center">
                        <Wallet size={16} />
                    </div>
                    <div className="flex-1">
                        <div className="text-sm font-medium text-stone-900">Cancellation savings tracked</div>
                        <div className="text-[12px] text-stone-600 mt-0.5">
                            {cancellations.count} subscription{cancellations.count === 1 ? "" : "s"} cancelled ·
                            &nbsp;<strong className="text-emerald-800">{fmtMoney(cancellations.total_annual_saving)}/yr</strong> saved
                        </div>
                    </div>
                </div>
            )}

            <SubscriptionIntelligencePanel onOpenSub={openEdit} />

            {candidatesLoaded && candidates.length > 0 && hasRole("admin", "finance") && (
                <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50/40" data-testid="sub-candidates">
                    <div className="px-4 py-3 border-b border-amber-200 flex items-center gap-2">
                        <Sparkles size={14} className="text-amber-700" />
                        <div className="text-sm font-medium text-stone-900">
                            {candidates.length} recurring charge{candidates.length === 1 ? "" : "s"} detected
                        </div>
                        <div className="text-[12px] text-stone-500">
                            &mdash; not yet in your subscription list. Add the ones you want to track.
                        </div>
                    </div>
                    <div className="divide-y divide-amber-100">
                        {candidates.slice(0, 12).map((c) => (
                            <div key={c.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`sub-cand-${c.id}`}>
                                <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${
                                    c.source === "curated"
                                        ? "bg-emerald-100 text-emerald-700"
                                        : "bg-violet-100 text-violet-700"
                                }`}>
                                    {c.source === "curated" ? <Check size={12} /> : <TrendingUp size={12} />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-stone-900 truncate">{c.name}</div>
                                    <div className="text-[11px] text-stone-500 truncate">
                                        {c.card_label || "—"} · {c.occurrences} charges · {c.months_seen?.length || 0} months · {c.source}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-mono-figure text-sm text-stone-900">{fmtMoney(c.amount)}</div>
                                    <div className="text-[10px] uppercase tracking-widest text-stone-500">{c.frequency}</div>
                                </div>
                                <Button size="sm" variant="outline" onClick={() => acceptCandidate(c)} data-testid={`sub-cand-accept-${c.id}`}>
                                    <Check size={13} className="mr-1" /> Add
                                </Button>
                                <button className="text-stone-400 hover:text-stone-700 text-xs px-2" onClick={() => dismissCandidate(c)} aria-label="Dismiss" data-testid={`sub-cand-dismiss-${c.id}`}>×</button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="dense-table w-full"><thead>
                            <tr><th>Name</th><th>Vendor</th><th>Card</th><th>Freq</th><th>Next Billing</th><th className="text-right">Amount</th><th>Auto</th><th>Active</th><th></th></tr>
                        </thead><tbody>
                            {filteredRows.map((s) => {
                                const c = cardMap[s.card_id];
                                return (
                                    <Fragment key={s.id}>
                                        <tr>
                                            <td className="font-medium">{s.name}</td>
                                            <td>{s.vendor || "—"}</td>
                                            <td>{c ? `${c.bank_name} · •••• ${c.last_4}` : "—"}</td>
                                            <td>{s.frequency}</td>
                                            <td className="tabular-nums">{fmtDate(s.next_billing_date)}</td>
                                            <td className="text-right tabular-nums">{fmtMoney(s.amount)}</td>
                                            <td>{s.auto_renewal ? "Yes" : "No"}</td>
                                            <td>{s.active ? "Active" : "Cancelled"}</td>
                                            <td className="text-right">
                                                {hasRole("admin", "finance") && (
                                                    <div className="inline-flex items-center gap-1">
                                                        <button
                                                            onClick={() => openEdit(s)}
                                                            title="Edit subscription"
                                                            className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900"
                                                            data-testid={`sub-edit-${s.id}`}
                                                        >
                                                            <Pencil size={13} />
                                                        </button>
                                                        {s.active && (
                                                            <button
                                                                onClick={() => setCancelTarget(s)}
                                                                title="Cancel subscription"
                                                                className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-700"
                                                                data-testid={`sub-cancel-${s.id}`}
                                                            >
                                                                <XCircle size={13} />
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => del(s.id, s.name)}
                                                            title="Delete subscription"
                                                            className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-600"
                                                            data-testid={`sub-delete-${s.id}`}
                                                        >
                                                            <Trash2 size={13} />
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                        <tr className="best-card-hint-row">
                                            <td colSpan={9} className="!py-0 !border-0">
                                                <BestCardHint subscriptionId={s.id} />
                                            </td>
                                        </tr>
                                    </Fragment>
                                );
                            })}
                            {filteredRows.length === 0 && (
                                <EmptyState.Row
                                    colSpan={9}
                                    icon={Repeat}
                                    title={tab === "cancelled" ? "No cancelled subscriptions yet." : "Track every recurring charge."}
                                    hint={tab === "cancelled"
                                        ? "Cancellations you record will appear here with the annual savings tracked automatically."
                                        : "Add a subscription manually or run Detect to scan your card expenses for recurring charges."}
                                    cta={hasRole("admin", "finance") && tab !== "cancelled"
                                        ? { to: "#", label: "Add a subscription", testId: "empty-subs-cta" }
                                        : null}
                                    demoTo="/subscriptions"
                                />
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Cancellation history */}
            {cancellations.events.length > 0 && (
                <div className="mt-6 bg-white border border-stone-200 rounded-lg overflow-hidden" data-testid="cancellations-history">
                    <div className="px-4 py-3 border-b border-stone-100 eyebrow">Recently cancelled</div>
                    <table className="dense-table w-full"><thead><tr><th>Cancelled on</th><th>Name</th><th>Vendor</th><th className="text-right">Was costing</th><th className="text-right">Saved / year</th><th>Reason</th></tr></thead><tbody>
                            {cancellations.events.slice(0, 12).map((e) => (
                                <tr key={e.id}>
                                    <td className="tabular-nums">{fmtDate(e.cancelled_on)}</td>
                                    <td className="font-medium">{e.name}</td>
                                    <td>{e.vendor || "—"}</td>
                                    <td className="text-right tabular-nums">{fmtMoney(e.amount)} · {e.frequency}</td>
                                    <td className="text-right tabular-nums text-emerald-700 font-medium">{fmtMoney(e.annual_saving)}</td>
                                    <td className="text-xs text-stone-500">{e.reason || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Cancel dialog */}
            <SubscriptionCancelDialog
                open={!!cancelTarget}
                onOpenChange={(v) => { if (!v) setCancelTarget(null); }}
                subscription={cancelTarget}
                onCancelled={() => {
                    setCancelTarget(null);
                    load();
                    api.get("/subscriptions/cancellations/history").then((r) => setCancellations(r.data)).catch(() => {});
                }}
            />
        </div>
    );
}
